import { useCallback, useMemo } from 'react';
import type { SaveData } from '../components/SaveProgressModal/types';
import type { FilePathResult } from '../types/save';
import { getAppropriateFilePath } from '../utils/fileNaming';
import { sendToTerminal } from '../lib/terminalLogger';
import { useUnifiedFeaturePermission } from './useUnifiedPermissions';

export type BuildOptions = {
  useCVIfVariavel?: boolean;
};

export function useFilePathBuilder() {
  const useInternalFilePath = useUnifiedFeaturePermission('general.use_internal_file_path');

  const buildFilePath = useCallback(
    // _role kept for backward compatibility with existing callers (ignored — use useInternalFilePath)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (data: SaveData, _role: string, opts: BuildOptions = {}): FilePathResult => {
      try {
        if (!data) {
          return { filePath: null, fileName: null, error: 'Dados de salvamento ausentes' };
        }

        const pedidoInfo = {
          orc_id: data.pedido?.orc_id,
          oit_id: data.pedido?.oit_id,
          cliente: data.cliente || {},
          material: { nome: data.material?.nome },
          produto: data.produto || {},
          regras: data.regras || {},
        };

        // Quantidade
        const qtdObjetos = data.totalObjetos || data.objetos?.length || 1;

        // Contração (variação do fluxo handleSave vs handleSaveComplete)
        const contraction =
          opts.useCVIfVariavel && data.configuracoes?.contracoesVariaveis
            ? 'CV'
            : (data.configuracoes?.contracao || '0px');

        // Entrega
        const delivery = data.configuracoes?.entrega || 'Hoje';

        // Normalização do horário de corte → "HH:mm"
        let horarioCorte = '15:00';
        const raw = data.regras?.horario_corte;
        if (typeof raw === 'string' && raw.includes(':')) {
          const parts = raw.split(':');
          if (parts.length >= 2) {
            horarioCorte = `${parts[0]}:${parts[1]}`;
          }
        }

        const diretorioNome = data.regras?.diretorio_nome;

        // Validações obrigatórias - dados devem vir da API
        if (!data.diretorios?.interno || data.diretorios.interno.trim() === '') {
          return { 
            filePath: null, 
            fileName: null, 
            error: 'diretorio_base_interno não encontrado nas regras da API' 
          };
        }

        if (!diretorioNome || diretorioNome.trim() === '') {
          return { 
            filePath: null, 
            fileName: null, 
            error: 'diretorio_nome não encontrado nas regras da API' 
          };
        }

        try { sendToTerminal('TEMPLATE:BUILDER_INPUT', { useInternalFilePath, diretorioInterno: data.diretorios.interno, diretorioExterno: data.diretorios.externo, diretorioNome, qtdObjetos, contraction, delivery, horarioCorte }); } catch {}

        const filePath = getAppropriateFilePath(
          useInternalFilePath,
          data.diretorios.interno,
          data.diretorios.externo || '',
          pedidoInfo,
          qtdObjetos,
          contraction,
          delivery,
          horarioCorte,
          diretorioNome
        );

        const fileName = filePath ? (filePath.split('\\').pop() || null) : null;

        try { sendToTerminal('TEMPLATE:BUILDER_OUTPUT', { filePath, fileName }); } catch {}
        return { filePath, fileName, error: null };
      } catch (e: any) {
        return { filePath: null, fileName: null, error: e?.message || 'Erro ao construir caminho do arquivo' };
      }
    },
    [useInternalFilePath]
  );

  return useMemo(
    () => ({
      buildFilePath,
    }),
    [buildFilePath]
  );
}